import React, {useEffect} from 'react';
import { useRoute, useNavigation } from '@react-navigation/native';
import {View, ActivityIndicator} from 'react-native';
import SuggestedItemSection from './suggestedItemSection';
import AssignmentToast from '../scanToCart/CartAssignmentDetailToast';
import CartAssignmentDetails from '../scanToCart/CartAssignmentDetailPrompt';
import UnScannedItemsList from './UnScannedItems';
import ScannedItemsList from './ScannedItemList';
import FocusBar from './FocusBar';
import FocusedListTabs from './FocusedListTabs';
import CartAssignmentPrompt from '../scanToCart/prompt';
import {useDispatch, useSelector} from 'react-redux';
import CommonPrompt from './CommonPrompt';
import audioService from '../../services/AudioService';
import ResponseView from '../scanpack/response_view';
import TTSEnablementPrompt from '../../commons/modal/ttsEnablementPrompt';
import { handleBarcodeInput, handleIsEmptyCart, resetScanStates, selectFocusedOrder, setBarcodeInput, setCurrentCartScanComplete, startPostScanningForOrder } from '../../actions/ScanToCartScanningActions';
import { fetchCarts, fetchCart, resetScanToCartStates } from '../../actions/scanToCartActions';
import useWidth from './useWidth';
import { FOCUS_DESKTOP_MIN_WIDTH } from './utils/responsiveUtils';
import ConfirmationDialog from '../../commons/modal/ConfirmationDialog';
import i18n from 'i18n-js';
import { getPlaceQuantity } from './utils/scanProgressUtils';
import { usePostScanResume } from '../postScanning/usePostScanResume';
import Toast, { useToast } from '../../commons/Toast';

const ScanToCartHome = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const {
    showCartAssignmentToast,
    showCartAssignmentDetailsPrompt,
    scanSuccess,
    scanError,
    isNotEmptyCart,
    currentCartScanComplete,
    placedItemConfirmation,
    nextPickItem,
    lastCartInput,
    previousNextPickItems,
    postScanLoading,
    activeCart
  } = useSelector(state => ({
    showCartAssignmentToast: state.scanToCart.showCartAssignmentToast,
    showCartAssignmentDetailsPrompt: state.scanToCart.showCartAssignmentDetailsPrompt,
    scanSuccess: state.scanToCartScanning.scanSuccess,
    scanError: state.scanToCartScanning.scanError,
    placedItemConfirmation: state.scanToCartScanning.placedItemConfirmation,
    isNotEmptyCart: state.scanToCartScanning.isNotEmptyCart,
    currentCartScanComplete: state.scanToCart.currentCartScanComplete,
    nextPickItem: state.scanToCart.nextPickItem,
    lastCartInput: state.scanToCartScanning.lastCartInput,
    previousNextPickItems: state.scanToCart.previousNextPickItems,
    postScanLoading: state.scanToCartScanning.isLoading,
    activeCart: state.scanToCart.activeCart,
  }));
  const dispatch = useDispatch();
  const { showToast, toastMessage, showToastMessage, hideToast } = useToast();

  const windowWidth = useWidth();
  const isDesktopWidth = windowWidth >= FOCUS_DESKTOP_MIN_WIDTH;
  const focusedOrder = useSelector(selectFocusedOrder);
  const isFocused = focusedOrder !== null;
  const isStackedFocus = isFocused && !isDesktopWidth;
  const getPaneStyle = columnStyle => ({
    backgroundColor: '#4A6179',
    ...columnStyle,
    display: isDesktopWidth ? 'block' : 'none'
  });
  useEffect(() => {
    const initializeAudio = async () => {
      try {
        await audioService.initialize();
      } catch (error) {
        console.error('Failed to initialize audio service:', error);
      }
    };

    initializeAudio();
    dispatch(fetchCarts());
    return () => {
     //TODO reset states
     dispatch(resetScanStates());
     dispatch(resetScanToCartStates())
     dispatch(setBarcodeInput(''))
    };
  }, []);
  const payload = {
    app: "app",
    scan_to_cart_enabled: true,
    state: "scanpack.rfo"
  }

  usePostScanResume(({
    postScanOrderId,
    postScanningModulesRemaining,
    postScanningToteInput,
    postScanningFromShippingLabel,
    resetCartFlag,
    awaitingCartRefresh
  }) => {
    if (!postScanOrderId) return false;
    if (resetCartFlag || awaitingCartRefresh) return false;
    dispatch(startPostScanningForOrder(postScanOrderId, postScanningModulesRemaining, postScanningToteInput, { fromShippingLabel: !!postScanningFromShippingLabel }));
    return true;
  }, ['postScanOrderId', 'postScanningModulesRemaining', 'postScanningToteInput', 'postScanningFromShippingLabel']);

  useEffect(() => {
    const { resetCartFlag, postScanningContinue, postScanningModulesRemaining } = route?.params || {};
    if (resetCartFlag && activeCart) {
      const cartId = activeCart.id || activeCart.cart_id;
      if (cartId) {
        const resumePending = !!postScanningContinue && Array.isArray(postScanningModulesRemaining);
        dispatch(setBarcodeInput(''));
        navigation.setParams({ resetCartFlag: false, awaitingCartRefresh: resumePending });
        dispatch(fetchCart(
          cartId,
          () => {
            if (resumePending) navigation.setParams({ awaitingCartRefresh: false });
          },
          { skipCompletionCheck: resumePending }
        ));
      }
    }
  }, [route?.params, activeCart]);

  useEffect(() => {
    const { showScannedMessage, showCancelledMessage } = route?.params || {};
    if (!showScannedMessage && !showCancelledMessage) return;
    showToastMessage(i18n.t(showScannedMessage ? 'scanPack.already_scanned' : 'scanPack.already_cancelled'));
    navigation.setParams({ showScannedMessage: false, showCancelledMessage: false, scannedOrder: undefined });
  }, [route?.params?.showScannedMessage, route?.params?.showCancelledMessage]);

  const getConfirmMessage = () => {
    if(nextPickItem?.tote){
      const currentTote = previousNextPickItems.length > 0 ? previousNextPickItems[0].tote : nextPickItem?.tote
      const placeQty = getPlaceQuantity(currentTote || null);
      return `Scan Tote "${currentTote?.tote_id}" again to confirm "${placeQty}" pieces placed.`
    }
  }
  
  return (
    <View
      testID="scanToCartHome"
      style={{
        height: windowWidth > 900 ? 'calc(100vh - 64px)' : 'calc(100vh - 40px)',
        overflow: 'auto'
      }}>
      {/* Cart assignment related toast and prompt */}
      {showCartAssignmentToast && <AssignmentToast />}
      {showCartAssignmentDetailsPrompt && <CartAssignmentDetails />}
      {/* Cart assignment related toast and prompt */}
      {isNotEmptyCart && (
        <CartAssignmentPrompt
          showPrompt={isNotEmptyCart}
          setShowPrompt={(value)=>dispatch(handleIsEmptyCart(value))}
          cartId={lastCartInput || ''}
          payload={payload}
        />
      )}

      {currentCartScanComplete.isVisible && (
        <ConfirmationDialog
          showButtons={false}
          title={''}
          message={i18n.t(currentCartScanComplete.messageKey)}
          showCloseIcon={currentCartScanComplete.showCloseIcon || false}
          onClose={() => dispatch(setCurrentCartScanComplete(false))}
        />
      )}

      {postScanLoading && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.25)', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}

      {placedItemConfirmation && (
        <ConfirmationDialog
          showButtons={false}
          showInput
          title={getConfirmMessage()}
          inputPlaceholder={'Please enter tote barcode'}
          showCloseIcon={false}
          onSubmit={(value)=>dispatch(handleBarcodeInput(true, value))}
        />
      )}

      <CommonPrompt />
      <Toast visible={showToast} message={toastMessage} onHide={hideToast} noModal />
      {(scanSuccess || scanError) && (
        <ResponseView
          orderCompleteAction={false}
          scanSuccessAction={scanSuccess}
          scanFailAction={scanError}
        />
      )}

      {/* Voice Picking - iOS TTS Enablement Prompt */}
      <TTSEnablementPrompt />

      {isDesktopWidth && <FocusBar />}

      <View style={isStackedFocus ? {flexDirection: 'column'} : {flex: 1, flexDirection: 'row'}}>
        {!isStackedFocus && (
          <View
            testID="unscannedPane"
            style={getPaneStyle({flex: 2})}>
            <UnScannedItemsList />
          </View>
        )}

        <SuggestedItemSection />

        {isStackedFocus ? (
          <FocusedListTabs key={focusedOrder.id} />
        ) : (
          <View
            testID="scannedPane"
            style={getPaneStyle({flex: 2, zIndex: -1})}>
            <ScannedItemsList />
          </View>
        )}
      </View>
    </View>
  );
};

export default ScanToCartHome;
